import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

export function PackageForm({ initial, onSave, onCancel, EQUIPMENT, galleries }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [dayRate, setDayRate] = useState(initial?.day_rate ?? '');
  const [equipmentItems, setEquipmentItems] = useState(initial?.equipment_items || []);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isSelected = (displayId) => equipmentItems.some(i => i.equipment === displayId);

  const toggleItem = (displayId) => {
    if (isSelected(displayId)) {
      setEquipmentItems(equipmentItems.filter(i => i.equipment !== displayId));
    } else {
      setEquipmentItems([...equipmentItems, { equipment: displayId, quantity: 1 }]);
    }
  };

  const updateQty = (displayId, qty) => {
    setEquipmentItems(equipmentItems.map(i =>
      i.equipment === displayId ? { ...i, quantity: Math.max(1, parseInt(qty) || 1) } : i
    ));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Package name is required'); return; }
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      equipment_items: equipmentItems,
      day_rate: dayRate !== '' ? parseFloat(dayRate) : null,
    });
  };

  const selectedCount = (sectionKey) => {
    const sectionImages = (galleries && galleries[sectionKey]) || [];
    return sectionImages.filter(img => isSelected(img.display_id)).length;
  };

  return (
    <Card className="pkg-form-card">
      <CardHeader><CardTitle>{initial ? 'Edit Package' : 'New Package'}</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="pkg-form-row">
            <div className="form-group">
              <Label className="form-label">Package Name</Label>
              <Input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Wedding Package" data-testid="pkg-name-input" />
            </div>
            <div className="form-group">
              <Label className="form-label">Day Rate (&pound;)</Label>
              <Input type="number" step="0.01" min="0" value={dayRate} onChange={e => setDayRate(e.target.value)} placeholder="e.g. 250.00" data-testid="pkg-rate-input" />
            </div>
          </div>
          <div className="form-group">
            <Label className="form-label">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Describe what this package includes..." data-testid="pkg-desc-input" />
          </div>
          <div className="form-group">
            <Label className="form-label">Select Equipment Items</Label>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', margin: '4px 0 12px' }}>
              Pick specific items from your galleries. {equipmentItems.length > 0 && <strong>{equipmentItems.length} item{equipmentItems.length !== 1 ? 's' : ''} selected</strong>}
            </p>
            <div className="pkg-section-list">
              {EQUIPMENT.map((eq) => {
                const sectionImages = (galleries && galleries[eq.key]) || [];
                const count = selectedCount(eq.key);
                const isExpanded = expandedSections[eq.key];
                return (
                  <div key={eq.key} className="pkg-section-group" data-testid={`pkg-section-${eq.key}`}>
                    <button
                      type="button"
                      className={`pkg-section-header ${count > 0 ? 'has-selected' : ''}`}
                      onClick={() => toggleSection(eq.key)}
                      data-testid={`pkg-section-toggle-${eq.key}`}
                    >
                      <span className="pkg-section-header-left">
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <span>{eq.label}</span>
                        <span className="pkg-section-count">({sectionImages.length})</span>
                      </span>
                      {count > 0 && <span className="pkg-section-badge">{count} selected</span>}
                    </button>
                    {isExpanded && (
                      <div className="pkg-section-items">
                        {sectionImages.length === 0 ? (
                          <p className="pkg-section-empty">No items uploaded yet</p>
                        ) : (
                          sectionImages.map((img) => {
                            const sel = equipmentItems.find(i => i.equipment === img.display_id);
                            return (
                              <div key={img.id} className={`pkg-item-row ${sel ? 'selected' : ''}`} data-testid={`pkg-item-${img.display_id}`}>
                                <div className="pkg-item-left" onClick={() => toggleItem(img.display_id)}>
                                  <input
                                    type="checkbox"
                                    checked={!!sel}
                                    onChange={() => toggleItem(img.display_id)}
                                    className="pkg-item-checkbox"
                                    data-testid={`pkg-check-${img.display_id}`}
                                  />
                                  <div className="pkg-item-thumb">
                                    <img src={getImageUrl(img.image_url)} alt={img.display_id} />
                                  </div>
                                  <div className="pkg-item-info">
                                    <span className="pkg-item-id">{img.display_id}</span>
                                    {img.caption && <span className="pkg-item-caption">{img.caption}</span>}
                                  </div>
                                </div>
                                {sel && (
                                  <div className="pkg-item-qty-wrap">
                                    <Label className="pkg-item-qty-label">Qty</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={sel.quantity}
                                      onChange={e => updateQty(img.display_id, e.target.value)}
                                      className="pkg-item-qty-input"
                                      data-testid={`pkg-qty-${img.display_id}`}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="pkg-form-actions">
            <Button type="submit" className="btn-primary" data-testid="pkg-save-btn">{initial ? 'Update Package' : 'Create Package'}</Button>
            <Button type="button" variant="outline" onClick={onCancel} data-testid="pkg-cancel-btn">Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function PackageAdminList({ packages, onEdit, onDelete }) {
  return (
    <div className="pkg-admin-list">
      {packages.map((pkg) => (
        <Card key={pkg.id} className="pkg-admin-card">
          <CardContent className="pkg-admin-content">
            <div className="pkg-admin-info">
              <div className="pkg-admin-header">
                <h3>{pkg.name}</h3>
                {pkg.day_rate != null && <span className="pkg-rate-badge">&pound;{pkg.day_rate}/day</span>}
              </div>
              {pkg.description && <p className="pkg-admin-desc">{pkg.description}</p>}
              {pkg.equipment_items && pkg.equipment_items.length > 0 && (
                <div className="pkg-tags">
                  {pkg.equipment_items.map((item, j) => (
                    <span key={j} className="pkg-tag">{item.equipment} &times;{item.quantity}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="pkg-admin-actions">
              <Button size="sm" variant="outline" onClick={() => onEdit(pkg)} data-testid={`edit-package-${pkg.id}`}>Edit</Button>
              <Button size="sm" variant="destructive" onClick={() => onDelete(pkg.id)} data-testid={`delete-package-${pkg.id}`}><Trash2 size={14} /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
